import type {FC} from 'react';

import {Observer} from 'mobx-react';

import type {TrackersStore} from '../../../../../stores/Trackers/Trackers.store';
import type {OnMoreIconClick} from '../../types';

import PlusIcon from '../../../../../../../../assets/icons/primary/Plus.svg';
import CloseIcon from '../../../../../../../../assets/icons/secondary/Close.svg';
import MoreIcon from '../../../../../../../../assets/icons/secondary/More.svg';
import SendIcon from '../../../../../../../../assets/icons/secondary/Send.svg';
import {Clickable} from '../../../../../../../../uikit/Clickable';
import {Icon} from '../../../../../../../../uikit/Icon';
import {Text} from '../../../../../../../../uikit/Text';
import {
  datetimeObjToISOString,
  formatDate,
} from '../../../../../../../../utils/datetime';
import * as S from '../../styles';
import {TestID} from '../../test_ids';

type Props = {
  commentText: string;
  handleSubmitComment: () => Promise<void>;
  isCommentInputVisible: boolean;
  onAddCommentClick: () => void;
  onCancelCommentClick: () => void;
  onMoreCommentClick: (
    e: Parameters<OnMoreIconClick>[0],
    entryId: string,
  ) => void;
  setCommentText: (value: string) => void;
  store: TrackersStore;
  t: (key: string) => string;
};

export const CommentBlock: FC<Props> = ({
  commentText,
  handleSubmitComment,
  isCommentInputVisible,
  onAddCommentClick,
  onCancelCommentClick,
  onMoreCommentClick,
  setCommentText,
  store,
  t,
}) => (
  <S.CommentBlock>
    <S.Row>
      <Text variant="h3" weight="bold">
        {t('shared:feedback_additional_comments')}
      </Text>
      {isCommentInputVisible ? (
        <Clickable
          onClick={onCancelCommentClick}
          testID={TestID.button_cancel_comment}
        >
          <S.IconContainer>
            <Icon color="icon3" svg={CloseIcon} />
            <Text color="text3">{t('shared:cancel')}</Text>
          </S.IconContainer>
        </Clickable>
      ) : (
        <Clickable
          onClick={onAddCommentClick}
          testID={TestID.button_add_comment}
        >
          <S.IconContainer>
            <Icon color="static.accent.trackers" svg={PlusIcon} />
            <Text color="static.accent.trackers">
              {t('shared:add_comment')}
            </Text>
          </S.IconContainer>
        </Clickable>
      )}
    </S.Row>
    {isCommentInputVisible ? (
      <S.CommentInputWrapper>
        <S.CommentInput
          containerStyle={{width: '100%'}}
          onChange={e => setCommentText(e.payload?.text ?? '')}
          onSubmit={handleSubmitComment}
          placeholder={t('shared:add_comment')}
          placeholderColor="text3"
          value={commentText}
        />
        <S.SendButton onClick={handleSubmitComment}>
          <S.SendIcon
            color={commentText.trim() ? 'static.accent.trackers' : 'icon3'}
            svg={SendIcon}
          />
        </S.SendButton>
      </S.CommentInputWrapper>
    ) : null}
    <Observer>
      {() =>
        store.diaryEntries.length === 0 ? null : (
          <S.CommentContainer>
            {store.diaryEntries.map(entry => (
              <S.CommentItem key={entry._id}>
                <S.Comment>
                  <S.CommentDate>
                    {formatDate(datetimeObjToISOString(entry.created), {
                      customFormat: 'MMM D, YYYY',
                    })}
                  </S.CommentDate>
                  <S.CommentContent>{entry.content ?? ''}</S.CommentContent>
                </S.Comment>
                <Clickable
                  onClick={e => onMoreCommentClick(e, entry._id)}
                  testID={TestID.button_delete_comment}
                >
                  <Icon color="icon3" svg={MoreIcon} />
                </Clickable>
              </S.CommentItem>
            ))}
          </S.CommentContainer>
        )
      }
    </Observer>
  </S.CommentBlock>
);
